import React from 'react';
import {
  LayoutDashboard,
  TrendingUp,
  Building,
  Cpu,
  BarChart3,
  Clock,
  Settings,
  X,
  Activity,
  Target,
  Landmark,
  Ship,
} from 'lucide-react';

type View = 'overview' | 'macro' | 'macro-monitor' | 'monetary' | 'trade' | 'fdi' | 'sectors' | 'peers' | 'events' | 'natex-score' | 'settings';

interface SidebarProps {
  activeView: View;
  onViewChange: (view: View) => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const navItems: { id: View; icon: React.ElementType; label: string }[] = [
  { id: 'overview', icon: LayoutDashboard, label: 'OVERVIEW' },
  { id: 'natex-score', icon: Target, label: 'SCORE' },
  { id: 'macro', icon: TrendingUp, label: 'MACRO' },
  { id: 'macro-monitor', icon: Activity, label: 'MONITOR' },
  { id: 'monetary', icon: Landmark, label: 'MONETARY' },
  { id: 'trade', icon: Ship, label: 'TRADE' },
  { id: 'fdi', icon: Building, label: 'FDI' },
  { id: 'sectors', icon: Cpu, label: 'SECTORS' },
  { id: 'peers', icon: BarChart3, label: 'PEERS' },
  { id: 'events', icon: Clock, label: 'EVENTS' },
  { id: 'settings', icon: Settings, label: 'SETTINGS' },
];

export const Sidebar: React.FC<SidebarProps> = ({
  activeView,
  onViewChange,
  isMobileOpen = false,
  onMobileClose,
}) => {
  return (
    <>
      {/* Mobile Sidebar - Slide-in drawer */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 w-52 bg-terminal-panel border-r border-terminal-border
          transform transition-transform duration-300 ease-in-out
          md:hidden
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Mobile Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-terminal-border">
          <span className="text-data-amber font-bold text-sm">SILK ROAD 2.0</span>
          <button
            onClick={onMobileClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-white active:bg-terminal-bg/50 transition-colors rounded"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Mobile Navigation */}
        <nav className="flex-1 py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onViewChange(item.id)}
                className={`w-full min-h-[48px] px-4 flex items-center gap-3 transition-colors relative active:bg-terminal-bg/30
                  ${isActive
                    ? 'text-data-amber bg-data-amber/10'
                    : 'text-gray-400 hover:text-white hover:bg-terminal-bg/50'
                  }`}
              >
                {isActive && (
                  <div className="absolute left-0 top-2 bottom-2 w-0.5 bg-data-amber" />
                )}
                <Icon size={20} />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

      </div>

      {/* Desktop/Tablet Sidebar - Always visible */}
      <div className="hidden md:flex w-12 lg:w-14 h-full bg-terminal-panel border-r border-terminal-border flex-col shrink-0">
        {/* Logo */}
        <div className="h-9 lg:h-10 flex items-center justify-center border-b border-terminal-border">
          <span className="text-data-amber font-bold text-xs lg:text-sm">SR</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onViewChange(item.id)}
                title={item.label}
                className={`w-full py-2 lg:py-2.5 px-1 flex flex-col items-center gap-0.5 transition-colors relative group
                  ${isActive
                    ? 'text-data-amber bg-data-amber/10'
                    : 'text-gray-500 hover:text-gray-300'
                  }`}
              >
                {isActive && (
                  <div className="absolute left-0 top-1 bottom-1 w-0.5 bg-data-amber" />
                )}
                <Icon size={16} className="lg:w-[18px] lg:h-[18px]" />
                <span className="text-[7px] lg:text-[8px] leading-none">{item.label}</span>
              </button>
            );
          })}
        </nav>

      </div>
    </>
  );
};

export default Sidebar;
